import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { simboloMoneda } from '../services/negocioActual';
import { listarPendientes, subirPendientes, descartarPendiente } from '../services/ventasPendientes';
import { mensajeAmigable } from '../utils/errores';

// Ventas hechas sin internet que esperan subir. Se suben solas al volver la
// señal (y cada 30 segundos mientras queden). Muestra cuántas faltan y, si la
// base rechazó alguna, deja reintentar o descartar.
const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fechaCorta = (iso) => new Date(iso).toLocaleString('es-HN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

export default function VentasPendientes({ onSubidas }) {
  const { negocio, sesion } = useAuth();
  const { mostrarToast } = useToast();
  const negocioId = negocio?.id;
  const userId = sesion?.user?.id;
  const [lista, setLista] = useState(() => listarPendientes(negocioId));
  const [abierto, setAbierto] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  const refrescar = useCallback(() => setLista(listarPendientes(negocioId)), [negocioId]);

  const subir = useCallback(async () => {
    if (!negocioId || !userId || !listarPendientes(negocioId).length || !navigator.onLine) return;
    setSubiendo(true);
    try {
      const r = await subirPendientes(negocioId, userId);
      if (r.subidas) {
        mostrarToast(`✅ ${r.subidas === 1 ? 'Se subió 1 venta' : `Se subieron ${r.subidas} ventas`} hecha${r.subidas === 1 ? '' : 's'} sin internet`, 'exito', 5000);
        onSubidas?.();
      }
      if (r.negativos.length) {
        mostrarToast(`⚠️ Revisa tu inventario: quedó en negativo ${r.negativos.join(', ')}`, 'error', 10000);
      }
      if (r.comisionFallida) {
        mostrarToast('Una comisión bancaria no se pudo registrar. Agrégala a mano en 💸 Gastos.', 'error', 7000);
      }
      if (r.conError) setAbierto(true);
    } catch (e) {
      // Se intenta de nuevo en el siguiente ciclo.
    } finally {
      setSubiendo(false);
      refrescar();
    }
  }, [negocioId, userId, mostrarToast, onSubidas, refrescar]);

  useEffect(() => {
    refrescar();
    subir();
    window.addEventListener('kontrol-pendientes', refrescar);
    window.addEventListener('online', subir);
    const reloj = setInterval(subir, 30000);
    return () => {
      window.removeEventListener('kontrol-pendientes', refrescar);
      window.removeEventListener('online', subir);
      clearInterval(reloj);
    };
  }, [refrescar, subir]);

  if (!lista.length) return null;
  const conError = lista.filter((v) => v.error).length;
  const total = lista.reduce((s, v) => s + Number(v.resumen?.total || 0), 0);

  const descartar = (v) => {
    if (!window.confirm(`¿Descartar esta venta de ${fmt(v.resumen?.total)}? No se registrará en Kontrol ni descontará del inventario.`)) return;
    descartarPendiente(negocioId, v.ticket);
  };

  return (
    <div style={{ background: conError ? '#FEF2F2' : '#EFF6FF', border: `1px solid ${conError ? '#FCA5A5' : '#93C5FD'}`, color: '#1C1830', borderRadius: '12px', padding: '10px 14px', marginBottom: '14px', fontSize: '14px', lineHeight: 1.5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <span>
          <b>🕓 {lista.length === 1 ? '1 venta' : `${lista.length} ventas`} sin subir</b> ({fmt(total)}).{' '}
          {subiendo ? 'Subiendo…' : (navigator.onLine ? (conError ? `${conError} con problema.` : 'Se suben en unos segundos.') : 'Se suben solas cuando vuelva la señal.')}
        </span>
        <button onClick={() => setAbierto(!abierto)} className="k-btn-soft" style={{ background: '#fff' }}>{abierto ? 'Ocultar' : 'Ver'}</button>
      </div>
      {abierto && (
        <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'grid', gap: '8px' }}>
          {lista.map((v) => (
            <li key={v.ticket} style={{ background: '#fff', borderRadius: '10px', padding: '8px 10px', border: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '13px' }}>{fechaCorta(v.creada)}{v.resumen?.cliente ? ` · ${v.resumen.cliente}` : ''}</span>
                <b>{fmt(v.resumen?.total)}</b>
              </div>
              <div style={{ fontSize: '13px' }}>{(v.resumen?.productos || []).join(', ')}</div>
              {v.error && (
                <div style={{ marginTop: '6px' }}>
                  <div style={{ color: '#B91C1C', fontSize: '13px' }}>No se pudo subir: {mensajeAmigable({ message: v.error })}</div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                    <button onClick={subir} className="k-btn-soft" disabled={subiendo}>🔄 Reintentar</button>
                    <button onClick={() => descartar(v)} className="k-btn-soft" style={{ color: '#B91C1C' }}>Descartar</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
